import React, { useMemo, useState } from 'react'
import { Modal } from 'antd'
import { Button } from '@surecompanies/core_components'
import isNil from 'lodash/isNil'
import {
  ModalSubtitle,
  ReviewActions,
  ReviewBulkBar,
  ReviewCard,
  ReviewCardHeader,
  ReviewCardTitle,
  ReviewGrid,
  ReviewGridEmail,
  ReviewGridHeadCell,
  ReviewReason,
  ReviewReasonStack,
  ReviewRow,
  ReviewRowMain,
  ReviewRowTitle,
  ReviewSectionTitle,
  SyncFieldHint,
} from './ClientBillingProfileManagement.styles'
import { RecipientRemovalNotice } from './RecipientRemovalNotice'
import { useHubspotReviewRemoval } from './useHubspotReviewRemoval'
import { joinNames } from '../UserManagement/userDisplay'
import type {
  ReviewCompanyRow,
  ReviewRecipientRow,
} from './hubspotSync.helpers'
import type { useHubspotSyncReview } from './useHubspotSyncReview'

interface HubspotSyncReviewCardProps {
  state: ReturnType<typeof useHubspotSyncReview>
  /** Companies the sync could not act on at all. Read-only. */
  companies: ReviewCompanyRow[]
  onClose: () => void
}

/** Said once below the people when a company would have nobody to invoice. */
const emptiedWarning = (companyNames: string[]) =>
  companyNames.length === 0
    ? null
    : `${joinNames(companyNames)} will have nobody getting ${
        companyNames.length === 1 ? 'its' : 'their'
      } invoices.`

/** Which removal is waiting to be confirmed, if any. */
type Pending = { kind: 'all' } | { kind: 'one'; row: ReviewRecipientRow }

/**
 * Recipients HubSpot no longer lists, deleted one at a time on the page. Each
 * delete is applied as it is made, so dismissing the card keeps what was
 * already removed and leaves the rest untouched.
 */
const HubspotSyncReviewCard = ({
  state,
  companies,
  onClose,
}: HubspotSyncReviewCardProps) => {
  // Both removals confirm in the centre of the screen, like removing a company
  // does. A popover hung off a link in the corner read as a stray tooltip.
  const [pending, setPending] = useState<Pending | null>(null)

  const removing = useMemo(() => {
    if (isNil(pending)) return []
    return pending.kind === 'all' ? state.rows : [pending.row]
  }, [pending, state.rows])
  const removal = useHubspotReviewRemoval(removing)
  const warning = emptiedWarning(state.companiesLeftEmpty(removing))

  const handleConfirm = () => {
    if (isNil(pending)) return
    if (pending.kind === 'all') state.handleDeleteAll(removal.revokeEmails)
    else state.handleDelete(pending.row, removal.revokeEmails)
    setPending(null)
  }

  return (
    <ReviewCard aria-label="Needs review">
      <ReviewCardHeader>
        <ReviewCardTitle>Needs review ({state.rows.length})</ReviewCardTitle>
        <ReviewBulkBar>
          <Button
            type="link"
            size="small"
            danger
            loading={state.isBusy}
            disabled={!state.canDeleteAll}
            onClick={() => setPending({ kind: 'all' })}
          >
            Remove all
          </Button>
          <span className="review-bulk-divider" aria-hidden="true" />
          <Button type="link" size="small" onClick={onClose}>
            Dismiss
          </Button>
        </ReviewBulkBar>
      </ReviewCardHeader>

      <ModalSubtitle>
        HubSpot no longer lists these addresses for these companies. Remove the
        ones that should stop receiving invoices. An address that should stay
        belongs back in HubSpot, or the next sync flags it again.
      </ModalSubtitle>

      {state.rows.length > 0 ? (
        <ReviewGrid>
          <ReviewGridHeadCell>Email</ReviewGridHeadCell>
          <ReviewGridHeadCell>Company</ReviewGridHeadCell>
          <ReviewGridHeadCell>Reason</ReviewGridHeadCell>
          <ReviewGridHeadCell />

          {state.rows.map((row) => (
            <React.Fragment key={row.key}>
              <ReviewGridEmail>{row.email}</ReviewGridEmail>
              <div>{row.companyName}</div>
              <ReviewReasonStack>
                <ReviewReason $tone="warning">
                  No longer listed in HubSpot
                </ReviewReason>
                {state.rowError(row.key) ? (
                  <ReviewReason $tone="error" role="alert">
                    Not removed: {state.rowError(row.key)}
                  </ReviewReason>
                ) : null}
              </ReviewReasonStack>
              <ReviewActions>
                <Button
                  type="link"
                  size="small"
                  danger
                  loading={state.isPending(row.key)}
                  // One write is shared by every row, so a second delete
                  // started mid-flight would strand the first one.
                  disabled={state.isBusy}
                  onClick={() => setPending({ kind: 'one', row })}
                >
                  Remove recipient
                </Button>
              </ReviewActions>
            </React.Fragment>
          ))}
        </ReviewGrid>
      ) : null}

      {companies.length > 0 ? (
        <>
          <ReviewSectionTitle>Companies we could not sync</ReviewSectionTitle>
          <SyncFieldHint>
            Nothing to decide here. The SureCo client each one points at needs
            fixing in HubSpot before the next sync.
          </SyncFieldHint>
          {companies.map((company) => (
            <ReviewRow key={company.key}>
              <ReviewRowMain>
                <ReviewRowTitle>{company.companyName}</ReviewRowTitle>
                <ReviewReason $tone="error">{company.reason}</ReviewReason>
              </ReviewRowMain>
            </ReviewRow>
          ))}
        </>
      ) : null}

      <Modal
        open={!isNil(pending)}
        centered
        width={600}
        title={
          pending?.kind === 'one'
            ? `Remove ${pending.row.email} from ${pending.row.companyName}?`
            : `Remove ${removing.length} ${removing.length === 1 ? 'recipient' : 'recipients'}?`
        }
        okText={pending?.kind === 'one' ? 'Remove' : 'Remove all'}
        okButtonProps={{ danger: true, disabled: removal.isChecking }}
        cancelText="Cancel"
        onOk={handleConfirm}
        onCancel={() => setPending(null)}
      >
        <RecipientRemovalNotice
          lines={removal.lines}
          canRevoke={removal.canRevoke}
          warning={warning}
        />
      </Modal>
    </ReviewCard>
  )
}

export { HubspotSyncReviewCard }
